<template>
  <v-card flat outlined>
    <v-card-title>
      {{ $t("machines.maintenance.service.log.title") }}
      <v-menu>
        <template v-slot:activator="{ on: menu }">
          <v-btn icon v-on="menu" class="grab-service-schedule-menu">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="exportCsv">
            <v-list-item-title class="body-2">
              <v-icon small>mdi-file-delimited</v-icon>
              {{ $t("common.actions.exportAsCSV") }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>
    <v-card-subtitle>
      <p class="subtitle">
        {{ $t("machines.maintenance.service.log.description") }}
      </p>
    </v-card-subtitle>
    <v-card-text class="text-black">
      <v-data-table
        :headers="headers"
        :items="maintenanceLogData"
        :isLoading="isLoading"
        item-key="key"
      >
        <template v-slot:[`item.date`]="{ item }">
          {{ item.date }}
        </template>

        <template v-slot:[`item.activity`]="{ item }">
          {{ item.activity }}
        </template>

        <template v-slot:[`item.engineHours`]="{ item }">
          {{ item.engineHours }}
        </template>

        <template v-slot:[`item.download`]="{ item, index }">
          <v-btn
            size="small"
            start
            v-if="item && item.uid && userHasServiceOrAdminRole"
            @click="downloadMaintenanceLog(item)"
            icon
            :loading="loading[index]"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>

        <template v-slot:bottom></template>
      </v-data-table>
    </v-card-text>

    <v-snackbar v-model="snackbar" :timeout="2000" :text="snackbarText" />
  </v-card>
</template>

<script>
import Exporter from "@/utils/csvexport"
import fetchClient from "@/utils/fetchClient"
import { ServiceProtocolType } from "@/utils/serviceProtocolType"
import formatter from "@/utils/formatter"

export default {
  props: ["machine"],
  components: {},

  data() {
    return {
      loading: [],
      logItem: {},
      snackbar: false,
      snackbarText: null,

      headers: [
        {
          text: this.$t(
            "machines.maintenance.service.log.dataTable.headers.date"
          ),
          value: "date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t(
            "machines.maintenance.service.log.dataTable.headers.activity"
          ),
          value: "activity",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t(
            "machines.maintenance.service.log.dataTable.headers.engineHours"
          ),
          value: "engineHours",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t(
            "machines.maintenance.service.log.dataTable.headers.download"
          ),
          value: "download",
          align: "center",
          sortable: false,
        },
      ],
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    tableHeaders() {
      return this.headers.filter((h) => h.key !== "uid")
    },
    isLoading() {
      return this.$store.getters.loadingMaintenanceLog
    },
    maintenanceLog() {
      return this.$store.getters.maintenanceLog ?? []
    },
    maintenanceLogData() {
      return this.maintenanceLog
        .map((log) => ({
          uid: log.uid,
          date: this.formatDate(log.date),
          engineHours: `${log.engineHours} h`,
          activity: this.getServiceProtocolTypeName(log.serviceProtocolTypeId),
        }))
        .sort((a, b) => {
          if (a.date < b.date) {
            return 1
          } else if (a.date > b.date) {
            return -1
          } else {
            return 0
          }
        })
    },
    userHasServiceOrAdminRole() {
      return this.$store.getters.isService || this.$store.getters.isAdmin
    },
  },

  methods: {
    fetchData() {
      const params = {
        tenantUid: this.$route.params.tenantUid,
        serialNo: this.$route.params.serialNo,
      }
      this.$store.dispatch("fetchMaintenanceLog", params)
    },
    exportCsv() {
      const rows = Exporter().exportMaintenanceLogs(
        this.machine,
        this.maintenanceLogData
      )
      this.snackbarText = this.$t("common.messages.recordsExported", {
        number: rows,
      })
      this.snackbar = true
    },
    downloadMaintenanceLog(item) {
      const ix = this.maintenanceLogData.indexOf(item)
      try {
        this.loading[ix] = true
        const url = `/api/v2/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/maintenance/serviceprotocols/${item.uid}/filegeneration`
        fetchClient()
          .getDataOrThrow(url)
          .then((response) => {
            try {
              const binaryData = atob(response)

              const byteArray = new Uint8Array(binaryData.length)
              for (let i = 0; i < binaryData.length; i++) {
                byteArray[i] = binaryData.charCodeAt(i)
              }

              const pdfBlob = new Blob([byteArray], { type: "application/pdf" })
              const date = new Date().toLocaleString("en-GB").substring(0, 10)

              const filename = `Brokk-Service_Protocol-${this.machine.serialNo}-${date}.pdf`

              let link = document.createElement("a")
              link.href = window.URL.createObjectURL(pdfBlob)
              link.download = filename
              link.click()
            } finally {
              this.loading[ix] = false
            }
          })
      } catch (error) {
        this.setError(error)
        this.loading[ix] = false
      }
    },
    getServiceProtocolTypeName(serviceProtocolTypeId) {
      switch (serviceProtocolTypeId) {
        case ServiceProtocolType.SERVICE_40H:
          return this.$t(
            "machines.maintenance.service.log.activities.service40h"
          )
        case ServiceProtocolType.SERVICE_250H:
          return this.$t(
            "machines.maintenance.service.log.activities.service250h"
          )
        case ServiceProtocolType.SERVICE_500H:
          return this.$t(
            "machines.maintenance.service.log.activities.service500h"
          )
        case ServiceProtocolType.SERVICE_1000H_YEARLY:
          return this.$t(
            "machines.maintenance.service.log.activities.service1000hOrYearly"
          )
        case ServiceProtocolType.WEEKLY_SERVICE:
          return this.$t(
            "machines.maintenance.service.log.activities.serviceWeekly"
          )
        case ServiceProtocolType.OTHER_SERVICE:
          return this.$t("machines.maintenance.service.log.activities.other")
      }
    },
    formatDate(dateStr) {
      return dateStr ? formatter().formatDate(new Date(dateStr)) : "-"
    },
  },
  watch: {
    maintenanceLogData() {
      if (!Array.isArray(this.maintenanceLogData)) {
        return ""
      }
      while (this.loading.length < this.maintenanceLogData.length) {
        this.loading.push(false)
      }
    },
  },
}
</script>

<style scoped>
.maintenance-log .v-data-table__wrapper tr {
  cursor: auto;
}
.divider {
  margin: auto;
}
</style>
